import React, {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {
  DashboardLayout,
  SearchInput,
  TableLayout,
  Loader,
  EmptyState,
  PageLimit,
  Pagination,
} from 'components';
import {baasMobile, eye, user, corruptedFile} from 'assets/images/images';
import CardSummary from 'components/CardSummary';
import {useNavigate} from 'react-router';
import {useQuery} from 'react-query';
import {fetchBAASCustomers} from 'requests/queries/baas';

function BaasCustomers() {
  const {userDetails} = useSelector(state => state?.auth);
  const navigate = useNavigate();
  const [query, setQuery] = useState({
    page: 1,
    length: 10,
  });
  const [searchText, setSearchText] = useState('');

  const customersParams = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      page: query.page,
      length: query.length,
      search: searchText,
    }),
    [userDetails, query.page, query.length, searchText],
  );

  const {data, isLoading} = useQuery(
    ['baas-customers', {params: {...customersParams}}],
    () => fetchBAASCustomers({...customersParams}),
    {
      meta: {preventGlobalError: true},
    },
  );

  const customers = data?.entity?.customers || [];

  const handlePageChange = page => {
    setQuery(prev => ({...prev, page}));
  };

  return (
    <DashboardLayout
      bg="bg-white80"
      topPadding="pt-8"
      xlLeftMargin="xl:ml-12"
      breadCrumbs={
        <div className="flex items-center">
          <img src={baasMobile} alt="" />
          <p className="ml-2 -mb-1">Customers</p>
        </div>
      }
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-grey text-[18px] font-medium">Customers</h3>
        <SearchInput
          placeholder="Search"
          className="w-[277px]"
          onChange={value => {
            setSearchText(value);
            setQuery(prev => ({...prev, page: 1}));
          }}
        />
      </div>
      <div className="mt-6">
        <CardSummary
          img={user}
          title="Total Customers"
          body={(data?.entity?.total ?? 0).toLocaleString()}
          className="!bg-white"
          loading={isLoading}
        />
      </div>
      {isLoading ? (
        <Loader height={45} />
      ) : customers.length === 0 ? (
        <EmptyState
          height={45}
          noBtn
          className="w-full"
          src={corruptedFile}
          heading="No Customers Found"
          body="Sorry, it appears there are no customers yet."
          maxWidth="max-w-[500px]"
        />
      ) : (
        <>
          <TableLayout negativeMargins className="w-full mt-5">
            <thead className="text-small font-semibold uppercase">
              <tr className="bg-white80">
                <td className="p-5 pl-12 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Name
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Date Created
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Email Address
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Phone Number
                </td>
                <td className="p-5 pl-0 "></td>
              </tr>
            </thead>
            <tbody className="text-[13px] text-body bg-white">
              {customers.map(customer => (
                <tr
                  key={customer.id}
                  className="text-left border-b bg-white border-grey60"
                >
                  <td className="p-4 pl-12 whitespace-nowrap">{`${customer.first_name} ${customer.last_name}`}</td>
                  <td className="p-4 pl-0 whitespace-nowrap">
                    {new Date(customer.date_created).toLocaleString()}
                  </td>
                  <td className="p-4 pl-0 whitespace-nowrap">
                    {customer.email || '-'}
                  </td>
                  <td className="p-4 pl-0 whitespace-nowrap">
                    {customer.phone_number || '-'}
                  </td>
                  <td className="p-4 pl-0 whitespace-nowrap flex items-center gap-4 justify-between">
                    <button
                      onClick={() =>
                        navigate(`/baas-customers/transactions/${customer.id}`)
                      }
                    >
                      <img src={eye} alt="eye" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableLayout>

          <div className="flex flex-wrap items-center justify-between gap-4 mt-12 sm:gap-0">
            <PageLimit
              onLimitChange={length => {
                setQuery(prevState => ({
                  ...prevState,
                  length,
                  page: 1,
                }));
              }}
              limit={query?.length}
              total={data?.entity?.total}
              length={customers.length}
            />

            <Pagination
              total={data?.entity?.total_pages}
              current={Number(data?.entity?.current_page)}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

export default BaasCustomers;
